import { useLocation } from "react-router";
import { ROUTES } from "@/components/shared/constant";
import { employeeShellFor, isEmployeePath, isEmployeeUserViewPath } from "@/routes/employeePaths";
import { useEmployeeGate } from "@/hooks/useEmployeeGate";
import { SkyEmployeeWordmark } from "@/views/employee/EmployeeBrandMarks";
import { OrgBrand } from "@/views/employee/branding/OrgBrand";

/**
 * The product lockup in the app header.
 *
 * Follows the product the user is currently in, so the bar reads as the thing
 * they are looking at. The header is `bg-[#242424]` in both themes, so both
 * lockups use their light-on-dark artwork regardless of the app theme.
 */
export function TenantLogo({ heightClass = "h-[30px]" }: { heightClass?: string }) {
  const location = useLocation();
  const { enabled: employeeOn } = useEmployeeGate();

  // Employee's own pages always; a shared page reached from Employee only while
  // Employee is on (the same rule as the sidebar, AppLayout).
  if (
    isEmployeePath(location.pathname) ||
    employeeShellFor(location.pathname, location.search, employeeOn)
  ) {
    return (
      <div className="flex items-center">
        <SkyEmployeeWordmark
          className={`${heightClass} w-auto`}
          surface="onDark"
        />
        {/* The organisation's own name and logo (P3-11), in skyEmployee only. */}
        <OrgBrand />
      </div>
    );
  }

  return (
    <div className="flex items-center">
      <img
        className={heightClass}
        src="/dist/img/skySupport-logo-light.png"
        alt="skySupport"
      />
    </div>
  );
}

/**
 * Where the header lockup should navigate — the home of the product the user
 * is in, so clicking a skyEmployee logo does not drop them into Service Desk.
 */
export function useHeaderLogoHome(): string {
  const location = useLocation();
  if (!isEmployeePath(location.pathname)) return ROUTES.HOME;
  // In whichever skyEmployee view the person is in.
  return isEmployeeUserViewPath(location.pathname) ? ROUTES.EMPLOYEE_MY_HOME : ROUTES.EMPLOYEE;
}
